import { useMemo, type ReactNode } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { fmtCurrency, fmtNumber } from "@/features/Dashboard/v2/ui/format";
import type { AIChart as AIChartSpec } from "../types/ai.types";

// ---------------------------------------------------------------------------
// AIChart — a pure presentational recharts view over the server's AIChart
// contract (run_report.result.chart / render_chart / ai:voice:chart).
//
//   bar         → grouped <BarChart>          (one <Bar> per series)
//   stacked_bar → <BarChart> with stackId     (segments share one stack)
//   line        → <LineChart>                 (2px lines, ringed dots)
//   area        → <AreaChart>                 (2px line + 12% wash)
//   pie         → donut <PieChart> + legend   (FIRST series only)
//
// Inline styles only (portal-safe, same as the rest of the AI feature).
// `dark` swaps the ink/grid for the voice overlay's deep-green background.
// ---------------------------------------------------------------------------

type SeriesFormat = NonNullable<AIChartSpec["series"][number]["format"]>;

interface Props {
  chart: AIChartSpec;
  /** Plot height in px — 240 (chat) / 300 (overlay) by default. */
  height?: number;
  /** Light ink + grid for the dark voice overlay. */
  dark?: boolean;
  /** When given, a × button sits on the title row (the overlay's card). */
  onDismiss?: () => void;
}

const MAX_CATEGORIES = 40;
const MAX_SERIES = 8;
const LABEL_MAX = 14;

/** Reports-v2 house hues (chartTheme.SERIES_COLORS), interleaved so adjacent
 *  series stay apart — the builder's greens-first rotation puts three greens
 *  side by side, which a stacked bar cannot survive. */
const LIGHT_SERIES = ["#218760", "#335cff", "#FF8447", "#8B5CF6", "#d9a441", "#06b6d4", "#ec4899", "#47B28F"];
/** The same hues stepped lighter for the overlay's dark surface. */
const DARK_SERIES = ["#47B28F", "#7d9bff", "#FF8447", "#b39dff", "#e0b24f", "#22d3ee", "#f472b6", "#a3d9c1"];

interface Theme {
  title: string;
  ink: string;
  muted: string;
  tick: string;
  grid: string;
  surface: string;
  cursor: string;
  tipBg: string;
  tipBorder: string;
  series: string[];
}

const LIGHT: Theme = {
  title: "var(--color-text-primary, #1a1a1a)",
  ink: "#051912",
  muted: "#697571",
  tick: "#697571",
  grid: "#eef2f0",
  surface: "#ffffff",
  cursor: "rgba(111,185,156,0.18)",
  tipBg: "rgba(255,255,255,0.97)",
  tipBorder: "#e6e8e7",
  series: LIGHT_SERIES,
};

const DARK: Theme = {
  title: "#eaf4ef",
  ink: "#eaf4ef",
  muted: "rgba(234,244,239,0.7)",
  tick: "rgba(234,244,239,0.75)",
  grid: "rgba(255,255,255,0.10)",
  surface: "#0b2e22",
  cursor: "rgba(255,255,255,0.07)",
  tipBg: "rgba(6,26,19,0.97)",
  tipBorder: "rgba(255,255,255,0.16)",
  series: DARK_SERIES,
};

// ---- formatters -----------------------------------------------------------

/** "m:ss" under an hour, "Xh Ym" after (mirrors the report builder). */
function fmtDuration(totalSec: number): string {
  const s = Math.max(0, Math.round(totalSec));
  if (s >= 3600) {
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    return m === 60 ? `${h + 1}h 0m` : `${h}h ${m}m`;
  }
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Full-precision value — tooltip, legend list, centre total. */
function fullFormatter(format: SeriesFormat | undefined): (v: number) => string {
  if (format === "currency") return fmtCurrency;
  if (format === "percent") return (v) => `${v.toFixed(1)}%`;
  if (format === "duration") return fmtDuration;
  return (v) =>
    Number.isInteger(v) ? fmtNumber(v) : v.toLocaleString("en-IN", { maximumFractionDigits: 1 });
}

/** Compact axis tick (12k / 1.2L / 1.2Cr) — ticks repeat, so they get the short form. */
function tickFormatter(format: SeriesFormat | undefined): (v: number) => string {
  if (format === "currency") return fmtCurrency;
  if (format === "percent") return (v) => `${Math.round(v)}%`;
  if (format === "duration") return fmtDuration;
  return (v) => {
    const abs = Math.abs(v);
    if (abs >= 1e7) return `${(v / 1e7).toFixed(abs >= 1e8 ? 0 : 1)}Cr`;
    if (abs >= 1e5) return `${(v / 1e5).toFixed(abs >= 1e6 ? 0 : 1)}L`;
    if (abs >= 1e3) return `${(v / 1e3).toFixed(abs >= 1e4 ? 0 : 1)}k`;
    return String(Math.round(v));
  };
}

const truncateLabel = (s: string): string =>
  s.length > LABEL_MAX ? `${s.slice(0, LABEL_MAX - 1)}…` : s;

const numOrNull = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

// ---- data model -----------------------------------------------------------

interface SeriesModel {
  key: string;
  name: string;
  color: string;
  fmt: (v: number) => string;
  format: SeriesFormat | undefined;
}

interface Row {
  label: string;
  [seriesKey: string]: string | number | null;
}

// ---- tooltip --------------------------------------------------------------

interface TipEntry {
  name?: string;
  value?: number | string;
  color?: string;
  dataKey?: string | number;
  payload?: { color?: string };
}

function ChartTip({
  active,
  payload,
  label,
  theme,
  fmtByKey,
  total,
}: {
  active?: boolean;
  payload?: TipEntry[];
  label?: ReactNode;
  theme: Theme;
  fmtByKey: Record<string, (v: number) => string>;
  /** Pie: the slice total, so each entry can show its share. */
  total?: number;
}) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div
      style={{
        background: theme.tipBg,
        border: `1px solid ${theme.tipBorder}`,
        borderRadius: 10,
        padding: "6px 10px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        maxWidth: 260,
      }}
    >
      {label !== undefined && label !== null && label !== "" && (
        <div style={{ fontSize: 11, fontWeight: 500, color: theme.muted, marginBottom: 3, wordBreak: "break-word" }}>{label}</div>
      )}
      {payload.map((entry, i) => {
        const v = entry.value;
        const fmt = fmtByKey[String(entry.dataKey ?? "")] ?? fmtByKey.__default;
        const display = typeof v === "number" ? fmt(v) : v == null ? "—" : String(v);
        const share = typeof v === "number" && total && total > 0 ? ` · ${((v / total) * 100).toFixed(1)}%` : "";
        return (
          <div key={`${entry.dataKey ?? i}-${entry.name ?? i}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, fontSize: 12, color: theme.ink }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: theme.muted, minWidth: 0 }}>
              <span aria-hidden style={{ width: 8, height: 8, borderRadius: 2, flex: "0 0 auto", background: entry.payload?.color ?? entry.color ?? theme.series[0] }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{entry.name}</span>
            </span>
            <span style={{ fontWeight: 500, fontVariantNumeric: "tabular-nums", flex: "0 0 auto" }}>
              {display}
              {share}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ---- component ------------------------------------------------------------

export default function AIChart({ chart, height, dark, onDismiss }: Props) {
  const theme = dark ? DARK : LIGHT;
  const plotHeight = height ?? (dark ? 300 : 240);
  const isPie = chart.type === "pie";

  const series = useMemo<SeriesModel[]>(
    () =>
      (Array.isArray(chart.series) ? chart.series : []).slice(0, MAX_SERIES).map((s, i) => ({
        key: `s${i}`,
        name: s?.name || `Series ${i + 1}`,
        color: theme.series[i % theme.series.length],
        fmt: fullFormatter(s?.format),
        format: s?.format,
      })),
    [chart.series, theme],
  );

  const rows = useMemo<Row[]>(() => {
    const cats = (Array.isArray(chart.categories) ? chart.categories : []).slice(0, MAX_CATEGORIES);
    return cats.map((c, i) => {
      const row: Row = { label: String(c ?? "") };
      chart.series?.slice(0, MAX_SERIES).forEach((s, si) => {
        row[`s${si}`] = numOrNull(s?.values?.[i]);
      });
      return row;
    });
  }, [chart.categories, chart.series]);

  const hasData = rows.length > 0 && series.length > 0 && rows.some((r) => series.some((s) => typeof r[s.key] === "number"));

  const fmtByKey = useMemo(() => {
    const map: Record<string, (v: number) => string> = { __default: fullFormatter(undefined) };
    for (const s of series) map[s.key] = s.fmt;
    if (series[0]) map.value = series[0].fmt;
    return map;
  }, [series]);

  const pie = useMemo(() => {
    if (!isPie || !series[0]) return null;
    const first = series[0];
    const data = rows.map((r, i) => ({
      name: r.label,
      value: Math.max(0, (r[first.key] as number | null) ?? 0),
      color: theme.series[i % theme.series.length],
    }));
    const total = data.reduce((acc, d) => acc + d.value, 0);
    return { first, data, total };
  }, [isPie, series, rows, theme]);

  const notes: string[] = [];
  if (chart.note) notes.push(chart.note);
  if (isPie && (chart.series?.length ?? 0) > 1 && series[0]) {
    notes.push(`Showing "${series[0].name}" only — a pie takes one series.`);
  }

  const titleRow = (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
      <div style={{ fontSize: dark ? 13 : 12, fontWeight: 600, color: theme.title, lineHeight: 1.3, wordBreak: "break-word" }}>
        📊 {chart.title || "Chart"}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close chart"
          style={{
            flex: "0 0 auto",
            background: dark ? "rgba(255,255,255,0.08)" : "var(--color-black-2, #f5f5f5)",
            color: theme.title,
            border: `1px solid ${dark ? "rgba(255,255,255,0.18)" : "var(--color-black-3, #e5e7eb)"}`,
            borderRadius: 999,
            fontSize: 16,
            lineHeight: 1,
            padding: "4px 10px",
            cursor: "pointer",
          }}
        >
          ×
        </button>
      )}
    </div>
  );

  const caption = notes.length ? (
    <div style={{ fontSize: 11, color: theme.muted, marginTop: 6, lineHeight: 1.4 }}>{notes.join(" ")}</div>
  ) : null;

  if (!hasData) {
    return (
      <div>
        {titleRow}
        <div style={{ padding: "18px 8px", textAlign: "center", fontSize: 12, color: theme.muted, border: `1px dashed ${theme.grid}`, borderRadius: 10 }}>
          No data to chart
        </div>
        {caption}
      </div>
    );
  }

  const legend =
    !isPie && series.length > 1 ? (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginTop: 6 }}>
        {series.map((s) => (
          <span key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: theme.muted }}>
            <span aria-hidden style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} />
            {s.name}
          </span>
        ))}
      </div>
    ) : null;

  const xLabel = chart.xLabel ? (
    <div style={{ textAlign: "center", fontSize: 11, color: theme.muted, marginTop: 2 }}>{chart.xLabel}</div>
  ) : null;

  /* ---- pie (donut + legend list) ---- */
  if (pie) {
    const { first, data, total } = pie;
    return (
      <div>
        {titleRow}
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: "1 1 180px", minWidth: 160, maxWidth: 260, height: Math.min(plotHeight, 260) }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  innerRadius="58%"
                  outerRadius="86%"
                  paddingAngle={2}
                  strokeWidth={0}
                  isAnimationActive={false}
                >
                  {data.map((d, i) => (
                    <Cell key={`${i}-${d.name}`} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTip theme={theme} fmtByKey={fmtByKey} total={total} />} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ pointerEvents: "none", position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 18, fontWeight: 500, letterSpacing: -0.4, color: theme.ink }}>{first.fmt(total)}</span>
              <span style={{ fontSize: 10, color: theme.muted, maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{first.name}</span>
            </div>
          </div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, flex: "1 1 160px", minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
            {data.map((d, i) => (
              <li key={`${i}-${d.name}`} title={d.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, fontSize: 12 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0, color: theme.muted }}>
                  <span aria-hidden style={{ width: 8, height: 8, borderRadius: 2, flex: "0 0 auto", background: d.color }} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</span>
                </span>
                <span style={{ flex: "0 0 auto", fontVariantNumeric: "tabular-nums", color: theme.ink }}>
                  {first.fmt(d.value)}
                  {total > 0 && <span style={{ marginLeft: 5, fontSize: 11, color: theme.muted }}>{((d.value / total) * 100).toFixed(1)}%</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
        {caption}
      </div>
    );
  }

  /* ---- bar / stacked_bar / line / area ---- */
  const stacked = chart.type === "stacked_bar";
  const axisFmt = tickFormatter(series[0].format);
  const tickStyle = { fontSize: 11, fill: theme.tick };
  const margin = { top: 8, right: 8, left: 0, bottom: 0 };
  const grid = <CartesianGrid vertical={false} stroke={theme.grid} strokeWidth={1} />;
  const xAxis = (
    <XAxis
      dataKey="label"
      tickFormatter={truncateLabel}
      tick={tickStyle}
      tickLine={false}
      axisLine={false}
      interval="preserveStartEnd"
      minTickGap={12}
    />
  );
  const yAxis = (
    <YAxis tick={tickStyle} tickLine={false} axisLine={false} width={46} tickFormatter={axisFmt} allowDecimals={false} />
  );
  const tooltip = (
    <Tooltip
      cursor={chart.type === "bar" || stacked ? { fill: theme.cursor } : { stroke: theme.grid, strokeWidth: 1 }}
      content={<ChartTip theme={theme} fmtByKey={fmtByKey} />}
    />
  );
  const showDots = rows.length <= 24;

  let plot: ReactNode;
  if (chart.type === "line") {
    plot = (
      <LineChart data={rows} margin={margin}>
        {grid}
        {xAxis}
        {yAxis}
        {tooltip}
        {series.map((s) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.name}
            stroke={s.color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            dot={showDots ? { r: 3, fill: s.color, stroke: theme.surface, strokeWidth: 2 } : false}
            activeDot={{ r: 5, fill: s.color, stroke: theme.surface, strokeWidth: 2 }}
            connectNulls={false}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    );
  } else if (chart.type === "area") {
    plot = (
      <AreaChart data={rows} margin={margin}>
        {grid}
        {xAxis}
        {yAxis}
        {tooltip}
        {series.map((s) => (
          <Area
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.name}
            stroke={s.color}
            strokeWidth={2}
            fill={s.color}
            fillOpacity={0.12}
            dot={false}
            activeDot={{ r: 5, fill: s.color, stroke: theme.surface, strokeWidth: 2 }}
            connectNulls={false}
            isAnimationActive={false}
          />
        ))}
      </AreaChart>
    );
  } else {
    plot = (
      <BarChart data={rows} margin={margin} barCategoryGap="24%" barGap={2}>
        {grid}
        {xAxis}
        {yAxis}
        {tooltip}
        {series.map((s) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.name}
            fill={s.color}
            stackId={stacked ? "stack" : undefined}
            radius={stacked ? undefined : ([4, 4, 0, 0] as [number, number, number, number])}
            maxBarSize={24}
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    );
  }

  return (
    <div>
      {titleRow}
      <div style={{ width: "100%", height: plotHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          {plot}
        </ResponsiveContainer>
      </div>
      {xLabel}
      {legend}
      {caption}
    </div>
  );
}
